/**
 * Phase 19 F23: client rules for PT/OT/ST standardized test results. These mirror the backend
 * (patientmr/therapyTests.js) so the form can flag problems before the server does.
 */
import type {
  StandardizedTestRowDto,
  TherapyDiscipline,
  TherapyTestFieldsDto,
} from '../../../dtos/patientmr/assessments/therapy-tests.dto';

export const MAX_TEST_ROWS = 20;
export const DELAY_SD_THRESHOLD = -1.5;
export const MAX_NOT_TESTED_REASON = 500;

/** Instruments offered per discipline; "Other" lets the user type the name. */
export const THERAPY_INSTRUMENTS: Record<TherapyDiscipline, readonly string[]> = {
  PT: ['PDMS-2', 'AIMS', 'Bayley-4 Motor', 'GMFM-66', 'GMFM-88', 'BOT-2', 'TIMP', 'Other'],
  OT: ['PDMS-2 Fine Motor', 'BOT-2', 'Bayley-4 Fine Motor', 'HELP', 'Beery VMI', 'Sensory Profile 2', 'Other'],
  ST: ['PLS-5', 'CELF-5', 'CELF-P3', 'Bayley-4 Language', 'Rossetti', 'REEL-4', 'CASL-2', 'PPVT-5', 'EVT-3', 'GFTA-3', 'Other'],
};

/** Label the server uses in the submit missing list (key `standardizedTests`). */
export const TESTS_REQUIRED_LABEL = 'At least one standardized test result, or the reason no standardized test was given';

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** SD from the mean, rounded to 2 places, or null when any input is missing (same as the server). */
export function sdFromMean(standardScore?: number | null, mean?: number | null, sd?: number | null): number | null {
  if (!isNum(standardScore) || !isNum(mean) || !isNum(sd) || sd <= 0) return null;
  return Math.round(((standardScore - mean) / sd) * 100) / 100;
}

export function isBelowThreshold(value: number | null): boolean {
  return value !== null && value <= DELAY_SD_THRESHOLD;
}

export function emptyTestRow(): StandardizedTestRowDto {
  return {
    instrument: '',
    subtest: '',
    rawScore: null,
    standardScore: null,
    mean: null,
    sd: null,
    percentile: null,
    ageEquivalent: '',
    testDate: '',
  };
}

/** A row counts toward the submit rule when it names an instrument and has any result. */
export function rowHasResult(row: StandardizedTestRowDto): boolean {
  return Boolean(row.instrument?.trim())
    && ([row.rawScore, row.standardScore, row.percentile].some(isNum) || Boolean(row.ageEquivalent?.trim()));
}

/** Field problems on one row that the server would reject (400 INVALID_THERAPY_TEST). */
export function rowErrors(row: StandardizedTestRowDto): string[] {
  const errors: string[] = [];
  const range = (v: number | null | undefined, label: string, min: number, max: number) => {
    if (isNum(v) && (v < min || v > max)) errors.push(`${label} must be from ${min} to ${max}`);
  };
  if (!row.instrument?.trim()) errors.push('Choose or type the instrument');
  range(row.standardScore, 'Standard score', 0, 300);
  range(row.mean, 'Mean', 0, 300);
  range(row.percentile, 'Percentile', 0, 100);
  if (isNum(row.sd) && (row.sd <= 0 || row.sd > 100)) errors.push('SD must be greater than 0 and at most 100');
  return errors;
}

/** Time in/out problems (server: HH:MM 24-hour, time out after time in). */
export function timeErrors(fields: TherapyTestFieldsDto): string[] {
  const errors: string[] = [];
  const { timeIn, timeOut } = fields;
  if (timeIn && !HHMM.test(timeIn)) errors.push('Time in must be HH:MM (24-hour)');
  if (timeOut && !HHMM.test(timeOut)) errors.push('Time out must be HH:MM (24-hour)');
  if (timeIn && timeOut && HHMM.test(timeIn) && HHMM.test(timeOut) && timeOut <= timeIn) {
    errors.push('Time out must be after time in');
  }
  return errors;
}

/**
 * Submit check for initial and recertification evaluations: at least one test result or the
 * reason none was given, plus the row and time checks. Discharge returns no errors.
 */
export function therapyTestErrors(assessmentType: string, fields: TherapyTestFieldsDto): string[] {
  if (assessmentType !== 'initial' && assessmentType !== 'recertification') return [];
  const rows = Array.isArray(fields.standardizedTests) ? fields.standardizedTests : [];
  const errors: string[] = [];
  if (!rows.some(rowHasResult) && !fields.notTestedReason?.trim()) errors.push(TESTS_REQUIRED_LABEL);
  rows.forEach((row, i) => {
    rowErrors(row).forEach(e => errors.push(`Standardized test ${i + 1}: ${e}`));
  });
  errors.push(...timeErrors(fields));
  return errors;
}

/** Pick the therapy test fields out of saved assessment data. */
export function pickTherapyTestFields(data: unknown): TherapyTestFieldsDto {
  if (!data || typeof data !== 'object') return {};
  const d = data as Record<string, unknown>;
  return {
    standardizedTests: Array.isArray(d.standardizedTests) ? (d.standardizedTests as StandardizedTestRowDto[]) : [],
    notTestedReason: typeof d.notTestedReason === 'string' ? d.notTestedReason : '',
    timeIn: typeof d.timeIn === 'string' ? d.timeIn : '',
    timeOut: typeof d.timeOut === 'string' ? d.timeOut : '',
    thstepsCurrentAttested: d.thstepsCurrentAttested === true,
    thstepsAttestedDate: typeof d.thstepsAttestedDate === 'string' ? d.thstepsAttestedDate : '',
  };
}

/** Legacy category values ("Earlier entry") that are not empty. */
export function legacyEntries(
  categories: Record<string, unknown> | undefined,
  keys: Array<{ key: string; label: string }>
): Array<{ label: string; value: string }> {
  return keys
    .map(({ key, label }) => ({ label, value: typeof categories?.[key] === 'string' ? (categories[key] as string).trim() : '' }))
    .filter(e => e.value !== '');
}
